// A tela da IA de um curso. UMA tela para todas elas.
//
// Hoje serve o Contabiliza.AI (Escola Contábil e Aprovação Contábil) e, quando o corpus da
// Perícia existir, serve a Peric.IA sem uma linha nova aqui — o card e o curso são
// parâmetro, não código. É a mesma regra da vitrine: "onde o mesmo card serve a cursos
// diferentes, ele é um módulo só".
//
// ---------------------------------------------------------------------------
// O desenho: "Espinha Navy" — o trilho da marca e o palco claro
// ---------------------------------------------------------------------------
// Dois planos que não se misturam (decisão de 21/08, pedido de Victor por "algo fora da
// caixa, com o histórico ao lado"): à esquerda um TRILHO navy vertical — o território da
// marca, com o quadriculado do painel-navy e o mastro (o símbolo do CF entre os dois arcos
// do Carregando) —, e à direita o PALCO claro onde a conversa corre. A leitura fica na luz
// de propósito: esta é a tela de maior volume de leitura contínua do hub, e a doutrina do
// index.css ("o público lê melhor em fundo claro") vale dobrado aqui.
//
// O vocabulário do mastro é regra de design, não enfeite: ARCO GIRANDO = PENSANDO,
// ARCO PARADO = PRONTO. É o único indicador de vida visível a distância, e é 100%
// vocabulário da marca — o C aberto do símbolo completado pelo arco.
//
// Dentro do trilho (e da faixa e da gaveta, que são o MESMO território) só entra cor de
// marca ou fixa: navy-ink, navy-mute, laranja. NENHUM token semântico (text-tinta,
// text-cinza, border-borda…) — é a cicatriz dos cards em escada do login: cor semântica
// sobre navy troca com o tema e some num deles.
//
// ---------------------------------------------------------------------------
// O curso não é detalhe: é QUAL corpus responde
// ---------------------------------------------------------------------------
// Decisão de Victor em 19/08: **corpus por curso, resolvido no clique**. O Contabiliza.AI é
// UM card em DOIS cursos, e cada um tem material próprio — quem entra pela Escola recebe o
// material da Escola, quem entra pela Aprovação recebe o da Aprovação, e nunca os dois.
// Isso não é preferência: é o que impede o aluno de receber, com citação e tudo, material de
// um curso que ele não comprou.
//
// Como o curso chega aqui, em ordem de preferência:
//   1. `?curso=` na url, posto por `aberturaDoCard` quando o aluno clica no card na tela de
//      um curso — o caso normal;
//   2. se ele só tem UM dos cursos deste card, é aquele, sem perguntar;
//   3. se tem mais de um e a url não diz, ele ESCOLHE. Não há palpite razoável aqui: as
//      respostas mudam conforme a escolha, e adivinhar seria mentir por conveniência.
//
// Por que o curso vai na QUERY e não no caminho: a rota interna é `/ferramenta/:slug/*`, e o
// `*` já é usado pelo MEI PRO para despachar as duas metades dele. Curso no caminho
// quebraria aquele despacho.
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useHub } from "../lib/dados";
import {
  excluirConversa,
  fixarConversa,
  lerMensagens,
  lerSugestoes,
  listarConversas,
  mensagemDaRecusa,
  perguntar,
  type Conversa,
  type Fonte,
  type Mensagem,
} from "../lib/ia";
import { TextoDaIA } from "../componentes/TextoDaIA";
import { SetaDireita } from "../componentes/ui";
import { IconeLixeira } from "../componentes/icones";
import BotaoAvaliarFerramenta from "../componentes/AvaliarFerramenta";
import simboloCf from "../assets/cf-simbolo.png";

type Fala = {
  chave: string;
  papel: "aluno" | "ia";
  texto: string;
  fontes?: Fonte[] | null;
  /** a resposta ainda está chegando */
  correndo?: boolean;
  aviso?: string | null;
};

// As sugestões vêm do BANCO (`ia_sugestao`, 000105), por curso. Antes eram três frases
// cravadas aqui — e como esta tela serve TODAS as IAs, a Peric.IA sugeria "Qual o prazo de
// entrega da ECD?" numa pós de perícia. Victor viu em 24/08.

export default function IaDoCurso({
  card,
  nome,
  artigo = "ao",
}: {
  card: string;
  nome: string;
  /**
   * O artigo definido que combina com o NOME — "Pergunte ao Contabiliza.AI",
   * "Pergunte à Peric.IA", "Pergunte à Central IBS·CBS".
   *
   * Era `nome === "Peric.IA" ? "à" : "ao"` aqui dentro, e isso é uma lista de
   * exceções escrita em comparação de string: a Central entrou em 14/09 e sairia
   * como "ao Central IBS·CBS". Gênero de nome próprio não se deduz — quem sabe é
   * quem batiza, e quem batiza é o despacho.
   */
  artigo?: "ao" | "à";
}) {
  const hub = useHub();
  const [params, setParams] = useSearchParams();
  const [falas, setFalas] = useState<Fala[]>([]);
  const [pergunta, setPergunta] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [conversaId, setConversaId] = useState<string | null>(null);
  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [gaveta, setGaveta] = useState(false);
  const rolo = useRef<HTMLDivElement>(null);
  // O aluno está com a leitura no fundo do rolo? Só então o autoscroll age —
  // rolar por cima de quem subiu para reler é roubar a leitura da mão dele.
  const preso = useRef(true);
  const caixa = useRef<HTMLTextAreaElement>(null);
  const abortar = useRef<AbortController | null>(null);

  const pronto = hub.fase === "pronto";
  const cardVitrine = pronto
    ? hub.hub.cardsDeCurso.find((c) => c.card_slug === card)
    : undefined;

  // TODOS os cursos do aluno — os livres E os da faculdade.
  //
  // `hub.cursos` traz só os livres: `dados.ts` separa `cursos` de `faculdade` porque a FBC é
  // outra prateleira na navegação. Só que a IA de curso não liga para essa divisão: a
  // Peric.IA pertence à PÓS de perícia, e lendo apenas `hub.cursos` a lista dela vinha
  // VAZIA — a tela caía no ramo "você tem mais de um curso" e não mostrava nenhum para
  // escolher. Foi o que Victor viu em 24/08 ao clicar no card.
  const todosOsCursos = useMemo(
    () => (pronto ? [...hub.hub.cursos, ...hub.hub.faculdade] : []),
    [pronto, hub],
  );

  // Os cursos deste card que são DELE e não estão trancados. `trancado` já vem mapeado de
  // `trancado_para_mim`: esta tela é a de quem está logado.
  const cursosPossiveis = useMemo(() => {
    if (!pronto || !cardVitrine) return [];
    return todosOsCursos.filter(
      (c) => cardVitrine.por_cursos.includes(c.slug) && !c.trancado,
    );
  }, [pronto, cardVitrine, todosOsCursos]);

  const cursoPedido = params.get("curso");
  const curso = useMemo(() => {
    if (cursoPedido && cursosPossiveis.some((c) => c.slug === cursoPedido)) return cursoPedido;
    if (cursosPossiveis.length === 1) return cursosPossiveis[0].slug;
    return null;
  }, [cursoPedido, cursosPossiveis]);

  const nomeDoCurso = cursosPossiveis.find((c) => c.slug === curso)?.nome ?? "";

  // Curso trancado é caso à parte e vale dizer: a IA AINDA responde, mas só sobre a
  // plataforma — o corpus de curso respeita a tranca do contrato (000068). Emudecer aqui
  // seria calar justamente quem precisa saber como destrancar.
  const todosTrancados = pronto && !!cardVitrine
    && cursosPossiveis.length === 0
    && todosOsCursos.some((c) => cardVitrine.por_cursos.includes(c.slug));

  useEffect(() => {
    if (!curso) return;
    listarConversas(card, curso).then(setConversas);
  }, [card, curso]);

  // As sugestões são do curso e vêm do banco. Enquanto não chegam, a lista fica vazia e a
  // tela mostra só o convite — melhor que piscar as frases de outro curso.
  const [sugestoes, setSugestoes] = useState<string[]>([]);
  useEffect(() => {
    if (!curso) { setSugestoes([]); return; }
    let vivo = true;
    lerSugestoes(curso).then((s) => { if (vivo) setSugestoes(s); });
    return () => { vivo = false; };
  }, [curso]);

  // Autoscroll condicionado: `preso` é atualizado no scroll do CONTÊINER, antes de o
  // conteúdo crescer — checar depois mentiria, porque o crescimento é o que tira o aluno
  // do fundo.
  useEffect(() => {
    const el = rolo.current;
    if (el && preso.current) el.scrollTop = el.scrollHeight;
  }, [falas]);

  const aoRolar = useCallback(() => {
    const el = rolo.current;
    if (el) preso.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
  }, []);

  const novaConversa = useCallback(() => {
    setFalas([]);
    setConversaId(null);
    setGaveta(false);
  }, []);

  const alternarFixada = useCallback(async (c: Conversa) => {
    await fixarConversa(c.id, !c.fixada);
    if (curso) listarConversas(card, curso).then(setConversas);
  }, [card, curso]);

  const excluir = useCallback(async (c: Conversa) => {
    await excluirConversa(c.id);
    // se a conversa aberta era esta, a tela volta ao vazio — mostrar falas de uma
    // conversa que não existe mais seria mentir que dá para continuá-la
    if (conversaId === c.id) {
      setFalas([]);
      setConversaId(null);
    }
    if (curso) listarConversas(card, curso).then(setConversas);
  }, [card, curso, conversaId]);

  const abrirConversa = useCallback(async (id: string) => {
    const msgs = await lerMensagens(id);
    setConversaId(id);
    setGaveta(false);
    preso.current = true;
    setFalas(msgs.map((m: Mensagem) => ({
      chave: m.id,
      papel: m.papel,
      texto: m.conteudo,
      fontes: m.fontes,
      aviso: mensagemDaRecusa(m.recusa),
    })));
  }, []);

  const enviar = useCallback(async (texto: string) => {
    const p = texto.trim();
    if (!p || ocupado || !curso) return;
    setPergunta("");
    if (caixa.current) caixa.current.style.height = "auto";
    setOcupado(true);
    preso.current = true;
    const chaveIa = `ia-${Date.now()}`;
    setFalas((f) => [
      ...f,
      { chave: `eu-${Date.now()}`, papel: "aluno", texto: p },
      { chave: chaveIa, papel: "ia", texto: "", correndo: true },
    ]);

    const ctrl = new AbortController();
    abortar.current = ctrl;
    const r = await perguntar({
      card, curso, pergunta: p, conversaId,
      sinal: ctrl.signal,
      aoReceber: (acumulado) =>
        setFalas((f) => f.map((x) => (x.chave === chaveIa ? { ...x, texto: acumulado } : x))),
    });
    abortar.current = null;
    setOcupado(false);

    if (!r.ok) {
      const aviso = mensagemDaRecusa(r.motivo) ?? "Não consegui responder agora.";
      setFalas((f) => f.map((x) => (
        x.chave === chaveIa ? { ...x, correndo: false, texto: "", aviso } : x
      )));
      return;
    }
    setFalas((f) => f.map((x) => (
      x.chave === chaveIa
        ? { ...x, correndo: false, texto: r.texto, fontes: r.fontes, aviso: mensagemDaRecusa(r.meta.recusa) }
        : x
    )));
    if (r.conversaId && r.conversaId !== conversaId) setConversaId(r.conversaId);
    if (curso) listarConversas(card, curso).then(setConversas);
  }, [card, curso, conversaId, ocupado]);

  /**
   * A pergunta que veio de FORA, na query (`?q=`).
   *
   * A Central IBS-CBS abre pela pergunta (decisão de Victor em 14/09): a caixa
   * grande fica na entrada da ferramenta e o envio traz o aluno para cá. Sem
   * isto, ele digitaria a pergunta duas vezes.
   *
   * O `q` é APAGADO da url antes de enviar, e isso não é limpeza: sem apagar,
   * recarregar a página refaz a pergunta — gastando token e duplicando a fala —,
   * e o botão "voltar" do navegador faria o mesmo. O `ref` é a segunda guarda,
   * para o efeito não disparar duas vezes no `StrictMode`.
   */
  const jaMandouDaQuery = useRef(false);
  useEffect(() => {
    const q = params.get("q");
    if (!q || !curso || ocupado || jaMandouDaQuery.current) return;
    jaMandouDaQuery.current = true;
    const limpa = new URLSearchParams(params);
    limpa.delete("q");
    setParams(limpa, { replace: true });
    void enviar(q);
  }, [params, curso, ocupado, enviar, setParams]);

  // ---- estados que não são a conversa
  if (hub.fase === "carregando") return <p className="p-6 text-cinza">Carregando…</p>;
  if (!pronto || !cardVitrine) {
    return (
      <p className="p-6 text-cinza">
        Este assistente não faz parte dos cursos que você tem hoje.{" "}
        <Link to="/" className="text-link underline">Voltar para o início</Link>
      </p>
    );
  }

  const trilho = (
    <TrilhoConteudo
      // O botão de AVALIAR (000326): a IA é aplicativo, sem a barra do alto das
      // outras ferramentas, e o lugar dele é o pé da coluna das conversas — que
      // no celular é a gaveta, e assim ele existe nas duas larguras.
      avaliar={
        cardVitrine?.card_tipo === "ferramenta" ? (
          <BotaoAvaliarFerramenta card={card} nome={cardVitrine.card_nome} sobreNavy className="w-full" />
        ) : null
      }
      nome={nome}
      nomeDoCurso={nomeDoCurso}
      ocupado={ocupado}
      esmaecido={!curso}
      conversas={curso ? conversas : []}
      conversaId={conversaId}
      aoNovaConversa={novaConversa}
      aoAbrirConversa={abrirConversa}
      aoFixar={alternarFixada}
      aoExcluir={excluir}
      aoTrocarCurso={cursosPossiveis.length > 1 && curso
        ? () => { setParams({}); setFalas([]); setConversaId(null); setGaveta(false); }
        : undefined}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <h1 className="sr-only">{nome}{nomeDoCurso ? ` — ${nomeDoCurso}` : ""}</h1>

      {/* A faixa mobile é o MESMO território do trilho, colapsado — não um segundo. */}
      <div className="trilho-navy flex items-center gap-3 px-4 py-2.5 lg:hidden">
        <Mastro tamanho={36} simbolo={18} girando={ocupado} sobreNavy />
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-bold text-navy-ink">{nome}</p>
          {nomeDoCurso && <p className="truncate text-xs text-navy-mute">{nomeDoCurso}</p>}
        </div>
        <button
          type="button"
          onClick={() => setGaveta(true)}
          aria-expanded={gaveta}
          className="min-h-11 rounded-full border border-navy-ink/20 px-4 text-sm font-medium text-navy-ink hover:bg-navy-ink/10"
        >
          Conversas
        </button>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[19rem_minmax(0,1fr)]">
        <aside className="hidden min-h-0 border-r border-navy-ink/10 lg:block" aria-label="Conversas">
          <div className="trilho-navy flex h-full min-h-0 flex-col">{trilho}</div>
        </aside>

        {/* O palco: o ÚNICO scroll é o rolo de mensagens — o composer é footer real do
            flex, nunca sticky, e por isso não pode sair do viewport. */}
        <div className="flex min-h-0 flex-col">
          <div ref={rolo} onScroll={aoRolar} className="min-h-0 flex-1 overflow-y-auto">
            <div className={`mx-auto w-full max-w-3xl px-4 py-6 ${falas.length === 0 ? "flex min-h-full flex-col items-center justify-center" : ""}`}>
              {!curso ? (
                todosTrancados ? (
                  <div className="mx-auto max-w-md text-center">
                    <p className="text-tinta">
                      O contrato do seu curso está pendente, e por isso eu ainda não consigo
                      consultar o material das aulas.
                    </p>
                    <p className="mt-2 text-cinza">
                      Assine o contrato na página do curso e o material fica disponível por aqui.
                    </p>
                    <Link to="/" className="pill-cta mt-6 inline-flex">Ver meus cursos</Link>
                  </div>
                ) : cursosPossiveis.length === 0 ? (
                  // Nem trancado, nem com curso para escolher: o card chegou à tela mas
                  // nenhum curso do aluno o sustenta. Antes deste ramo, a tela mostrava
                  // "você tem mais de um curso" com a lista VAZIA — afirmação falsa e nada
                  // para clicar, que foi o que Victor viu. Texto que descreve uma lista tem
                  // de sair quando a lista não existe.
                  <div className="mx-auto w-full max-w-md text-center">
                    <p className="text-tinta">
                      Não encontrei o curso deste assistente na sua conta. Se você acabou de
                      comprar, ele aparece assim que a matrícula for processada.
                    </p>
                    <Link to="/" className="pill-cta mt-6 inline-flex">Ver meus cursos</Link>
                  </div>
                ) : (
                  <div className="mx-auto w-full max-w-md">
                    <p className="text-center text-tinta">
                      Você tem mais de um curso com este assistente. Sobre qual deles você
                      quer perguntar?
                    </p>
                    <p className="mt-1 text-center text-sm text-cinza">
                      A escolha muda o material consultado: cada curso tem o seu.
                    </p>
                    <div className="mt-6 grid gap-3">
                      {cursosPossiveis.map((c, i) => (
                        <button
                          key={c.slug}
                          type="button"
                          onClick={() => setParams({ curso: c.slug })}
                          style={{ "--i": i } as React.CSSProperties}
                          className="porta surface rise flex items-center justify-between px-4 py-3.5 text-left"
                        >
                          <span className="font-medium text-titulo">{c.nome}</span>
                          <SetaDireita className="shrink-0 text-laranja-texto" />
                        </button>
                      ))}
                    </div>
                  </div>
                )
              ) : falas.length === 0 ? (
                <>
                  <div className="relative grid place-items-center">
                    <span
                      className="halo-ia motion-safe:animate-[cf-halo_4s_ease-in-out_infinite]"
                      aria-hidden="true"
                    />
                    <Mastro tamanho={88} simbolo={40} ambiente />
                  </div>
                  <p className="mt-6 text-center font-display text-2xl font-bold text-titulo">
                    Pergunte {artigo} {nome}
                  </p>
                  <p className="mt-2 max-w-md text-center text-cinza">
                    Respondo só pelo material de{" "}
                    <strong className="font-medium">{nomeDoCurso}</strong> e sobre a
                    plataforma — quando não encontro, eu digo, em vez de inventar.
                  </p>
                  <div className="mt-8 grid w-full gap-3 sm:grid-cols-3">
                    {sugestoes.map((s, i) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => enviar(s)}
                        style={{ "--i": i } as React.CSSProperties}
                        className="porta surface rise flex flex-col justify-between gap-3 p-4 text-left"
                      >
                        <span className="text-sm text-tinta">{s}</span>
                        <SetaDireita className="shrink-0 text-laranja-texto" />
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-col gap-7">
                  {falas.map((f) => (
                    f.papel === "aluno" ? (
                      <article
                        key={f.chave}
                        className="rise ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-azul px-4 py-2.5 text-navy-ink ring-1 ring-navy-ink/15"
                      >
                        <p className="whitespace-pre-wrap">{f.texto}</p>
                      </article>
                    ) : (
                      <article
                        key={f.chave}
                        className="rise grid max-w-[92%] grid-cols-[2rem_minmax(0,1fr)] gap-3"
                      >
                        <span className="grid size-8 place-items-center rounded-full bg-tile" aria-hidden="true">
                          <img src={simboloCf} alt="" className="size-4" />
                        </span>
                        {/* UM aria-live só, na fala que corre — no contêiner de todas ele
                            releria a conversa inteira a cada token. */}
                        <div aria-live={f.correndo ? "polite" : undefined}>
                          {f.texto && <TextoDaIA texto={f.texto} />}
                          {f.correndo && f.texto && <span className="caret-ia" aria-hidden="true" />}
                          {f.correndo && !f.texto && (
                            <p className="text-sm text-cinza motion-safe:animate-[cf-varredura_2.4s_linear_infinite] motion-safe:bg-[linear-gradient(90deg,var(--color-cinza)_35%,var(--color-azul)_50%,var(--color-cinza)_65%)] motion-safe:bg-[length:220%_100%] motion-safe:bg-clip-text motion-safe:text-transparent">
                              Procurando no material…
                            </p>
                          )}
                          {f.aviso && (
                            <p className="mt-2 rounded-lg bg-alerta-fundo px-3 py-2 text-sm text-alerta-tinta">
                              {f.aviso}
                            </p>
                          )}
                          {!!f.fontes?.length && (
                            <div className="mt-3 border-t border-borda pt-2.5">
                              <p className="text-[11px] font-semibold uppercase tracking-wide text-cinza">
                                De onde saiu
                              </p>
                              {/* Chips SEM hover de porta: fonte ainda não tem destino, e
                                  chip que parece clicável e não é, mente. */}
                              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                                {f.fontes.map((fo, i) => (
                                  <li
                                    key={i}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-borda bg-superficie-2 px-2.5 py-1 text-xs text-tinta"
                                  >
                                    <IconeLivro className="size-3.5 shrink-0 text-laranja-texto" />
                                    {fo.escopo === "plataforma" && (
                                      <span className="font-semibold text-link">Plataforma</span>
                                    )}
                                    <span>{fo.titulo}</span>
                                    {fo.aula_titulo && (
                                      <span className="text-cinza">· aula “{fo.aula_titulo}”</span>
                                    )}
                                    {fo.pagina_inicio ? (
                                      <span className="text-cinza">· pág. {fo.pagina_inicio}</span>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </article>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>

          {curso && (
            <div className="mx-auto w-full max-w-3xl px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <form
                onSubmit={(e) => { e.preventDefault(); enviar(pergunta); }}
                className="surface flex items-end gap-2 p-2 transition focus-within:border-laranja/60 focus-within:shadow-[0_0_0_3px_rgb(255_153_51/0.12)]"
              >
                <textarea
                  ref={caixa}
                  value={pergunta}
                  onChange={(e) => {
                    setPergunta(e.target.value);
                    // auto-cresce até o max-h; o CSS segura o resto com overflow
                    e.target.style.height = "auto";
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
                  }}
                  onKeyDown={(e) => {
                    // Enter envia, Shift+Enter quebra linha: é o que a mão do usuário
                    // espera de um chat, e o textarea existe para a pergunta longa caber.
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(pergunta); }
                  }}
                  rows={1}
                  maxLength={1500}
                  placeholder={nomeDoCurso ? `Pergunte sobre ${nomeDoCurso}…` : "Escreva a sua pergunta…"}
                  className="max-h-40 flex-1 resize-none bg-transparent px-3 py-2.5 text-tinta caret-laranja outline-none placeholder:text-cinza"
                />
                {/* O contador só aparece perto do limite: permanente é ansiedade grátis. */}
                {pergunta.length >= 1200 && (
                  <span className={`pb-2.5 text-xs tabular-nums ${pergunta.length >= 1400 ? "text-laranja-texto" : "text-cinza"}`}>
                    {pergunta.length.toLocaleString("pt-BR")}/1.500
                  </span>
                )}
                {ocupado ? (
                  <button
                    type="button"
                    onClick={() => abortar.current?.abort()}
                    aria-label="Parar a resposta"
                    className="btn-contorno size-11 shrink-0 !p-0"
                  >
                    <svg viewBox="0 0 24 24" className="size-3" aria-hidden="true">
                      <rect x="5" y="5" width="14" height="14" rx="2" fill="currentColor" />
                    </svg>
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!pergunta.trim()}
                    aria-label="Enviar pergunta"
                    className="pill-cta size-11 shrink-0 !p-0"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="size-5" aria-hidden="true">
                      <path d="M12 19V5M5 12l7-7 7 7" />
                    </svg>
                  </button>
                )}
              </form>
              <p className="pb-1 pt-2 text-center text-xs text-cinza">
                As respostas saem do material do curso e podem errar. Confira o que for para cliente.
              </p>
            </div>
          )}
        </div>
      </div>

      <GavetaConversas aberta={gaveta} aoFechar={() => setGaveta(false)}>
        {trilho}
      </GavetaConversas>
    </div>
  );
}

/**
 * O conteúdo do trilho — usado DUAS vezes com o mesmo território: na coluna fixa do
 * desktop e dentro da gaveta do celular. Só cor de marca ou fixa aqui dentro.
 *
 * Excluir pede DOIS cliques no mesmo botão (o primeiro vira "confirmar"): apagar uma
 * conversa é irreversível e o alvo tem 28px — um modal seria peso demais para o caso, e
 * clique único seria perda de histórico por tremida de mão. Sair da linha desarma.
 */
function TrilhoConteudo({
  nome,
  nomeDoCurso,
  ocupado,
  esmaecido,
  conversas,
  conversaId,
  aoNovaConversa,
  aoAbrirConversa,
  aoFixar,
  aoExcluir,
  aoTrocarCurso,
  avaliar = null,
}: {
  avaliar?: React.ReactNode;
  nome: string;
  nomeDoCurso: string;
  ocupado: boolean;
  esmaecido: boolean;
  conversas: Conversa[];
  conversaId: string | null;
  aoNovaConversa: () => void;
  aoAbrirConversa: (id: string) => void;
  aoFixar: (c: Conversa) => void;
  aoExcluir: (c: Conversa) => void;
  aoTrocarCurso?: () => void;
}) {
  const [confirmando, setConfirmando] = useState<string | null>(null);
  return (
    <>
      <div className={`mx-auto mt-6 ${esmaecido ? "opacity-60" : ""}`}>
        <Mastro tamanho={64} simbolo={28} girando={ocupado} sobreNavy />
      </div>
      <p className="mt-3 text-center font-display text-lg font-bold text-navy-ink">{nome}</p>
      {nomeDoCurso && <p className="mt-0.5 text-center text-xs text-navy-mute">{nomeDoCurso}</p>}

      <button type="button" onClick={aoNovaConversa} className="pill-cta mx-4 mt-5">
        Nova conversa
      </button>

      <nav className="mt-5 min-h-0 flex-1 overflow-y-auto px-2 pb-2" aria-label="Conversas anteriores">
        <p className="px-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy-mute">
          Conversas
        </p>
        {conversas.length === 0 ? (
          <p className="mt-2 px-2 text-sm text-navy-mute">As suas conversas ficam aqui.</p>
        ) : (
          <ul className="mt-2 grid gap-0.5">
            {conversas.map((c) => (
              // `min-w-0`: item de grid não encolhe abaixo do conteúdo por padrão, e um
              // título longo alargava o trilho inteiro até nascer scroll horizontal.
              <li
                key={c.id}
                onMouseLeave={() => { if (confirmando === c.id) setConfirmando(null); }}
                className={`group relative flex min-w-0 items-center rounded-lg ${
                  c.id === conversaId ? "bg-navy-ink/10" : "hover:bg-navy-ink/5"
                }`}
              >
                {c.id === conversaId && (
                  <span className="absolute inset-y-2 left-0 w-[3px] rounded-r bg-laranja" aria-hidden="true" />
                )}
                <button
                  type="button"
                  onClick={() => aoAbrirConversa(c.id)}
                  aria-current={c.id === conversaId ? "true" : undefined}
                  className={`min-w-0 flex-1 truncate px-3 py-2.5 text-left text-sm ${
                    c.id === conversaId ? "text-navy-ink" : "text-navy-mute group-hover:text-navy-ink"
                  }`}
                >
                  {c.fixada && (
                    <IconePino className="mr-1.5 inline size-3 -translate-y-px text-laranja" preenchido />
                  )}
                  {c.titulo}
                </button>
                {/* As ações aparecem no hover no desktop e ficam sempre visíveis no
                    celular (a gaveta não tem hover). Confirmação armada segura a linha
                    visível — sumir com o botão no meio do gesto seria desarmar sozinho. */}
                <div
                  className={`flex shrink-0 items-center gap-0.5 pr-1.5 ${
                    confirmando === c.id
                      ? ""
                      : "lg:opacity-0 lg:group-hover:opacity-100 lg:group-focus-within:opacity-100"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => aoFixar(c)}
                    aria-label={c.fixada ? "Desafixar conversa" : "Fixar conversa"}
                    title={c.fixada ? "Desafixar" : "Fixar no topo"}
                    className="grid size-7 place-items-center rounded-md text-navy-mute hover:bg-navy-ink/10 hover:text-navy-ink"
                  >
                    <IconePino className="size-3.5" preenchido={c.fixada} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirmando === c.id) { setConfirmando(null); aoExcluir(c); }
                      else setConfirmando(c.id);
                    }}
                    aria-label={confirmando === c.id ? "Confirmar exclusão" : "Excluir conversa"}
                    title={confirmando === c.id ? "Clique de novo para excluir" : "Excluir"}
                    className={`grid size-7 place-items-center rounded-md ${
                      confirmando === c.id
                        ? "bg-laranja text-azul"
                        : "text-navy-mute hover:bg-navy-ink/10 hover:text-navy-ink"
                    }`}
                  >
                    <IconeLixeira className="size-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </nav>

      {aoTrocarCurso && (
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={aoTrocarCurso}
            className="text-xs text-navy-mute underline underline-offset-2 hover:text-navy-ink"
          >
            Trocar de curso
          </button>
        </div>
      )}
      {avaliar && <div className="border-t border-navy-ink/10 px-4 py-3">{avaliar}</div>}
    </>
  );
}

/**
 * O mastro: o símbolo do CF (o PNG oficial — a marca não se redesenha) entre os dois arcos
 * do Carregando. ARCO GIRANDO = PENSANDO, ARCO PARADO = PRONTO — em repouso os arcos param
 * em posições assimétricas e só o símbolo respira; `ambiente` (o estado vazio) gira em
 * ritmo lento, de presença.
 */
function Mastro({
  tamanho,
  simbolo,
  girando = false,
  ambiente = false,
  sobreNavy = false,
}: {
  tamanho: number;
  simbolo: number;
  girando?: boolean;
  ambiente?: boolean;
  sobreNavy?: boolean;
}) {
  const externo = girando
    ? "motion-safe:animate-[cf-orbita_1.4s_linear_infinite]"
    : ambiente
      ? "motion-safe:animate-[cf-orbita_14s_linear_infinite]"
      : "-rotate-45";
  const interno = girando
    ? "motion-safe:animate-[cf-orbita_2.6s_linear_infinite_reverse]"
    : ambiente
      ? "motion-safe:animate-[cf-orbita_22s_linear_infinite_reverse]"
      : "rotate-[120deg]";
  return (
    <div
      className="relative grid place-items-center"
      style={{ width: tamanho, height: tamanho }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 100 100" className="absolute inset-0 size-full">
        <circle
          cx="50" cy="50" r="46" fill="none"
          stroke="var(--color-laranja)" strokeWidth="2.5" strokeLinecap="round"
          strokeDasharray="72 217"
          className={`origin-center ${externo}`}
        />
        <circle
          cx="50" cy="50" r="38" fill="none"
          stroke="var(--color-azul-medio)" strokeWidth="1.5" strokeLinecap="round"
          strokeDasharray="30 209"
          opacity={sobreNavy ? 0.75 : 0.55}
          className={`origin-center ${interno}`}
        />
      </svg>
      <img
        src={simboloCf}
        alt=""
        style={{ width: simbolo, height: simbolo }}
        className="motion-safe:animate-[cf-respira_3.5s_ease-in-out_infinite]"
      />
    </div>
  );
}

/**
 * A gaveta de conversas do celular. `<dialog>` nativo pela mesma razão do ModalVigencias:
 * Esc, armadilha de foco, aria-modal e devolução do foco vêm de graça — reimplementar em
 * div costuma ficar pela metade.
 */
function GavetaConversas({
  aberta,
  aoFechar,
  children,
}: {
  aberta: boolean;
  aoFechar: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dlg = ref.current;
    if (!dlg) return;
    if (aberta && !dlg.open) dlg.showModal();
    if (!aberta && dlg.open) dlg.close();
  }, [aberta]);

  return (
    <dialog
      ref={ref}
      onClose={aoFechar}
      onClick={(e) => {
        if (e.target === ref.current) aoFechar();
      }}
      aria-label="Conversas"
      className="trilho-navy m-0 h-dvh max-h-dvh w-80 max-w-[85vw] p-0 backdrop:bg-azul/40 backdrop:backdrop-blur-sm motion-safe:animate-[cf-desliza_0.25s_ease-out] lg:hidden"
    >
      <div className="flex h-full min-h-0 flex-col">
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar"
          className="absolute right-2 top-2 z-10 grid size-11 place-items-center rounded-full text-navy-mute hover:bg-navy-ink/10 hover:text-navy-ink"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="size-5" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
        {children}
      </div>
    </dialog>
  );
}

/** Alfinete das conversas fixadas. Dimensão intrínseca em 1em (a cicatriz das 34 setas). */
function IconePino({ className, preenchido = false }: { className?: string; preenchido?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={preenchido ? "currentColor" : "none"}
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      width="1em" height="1em" className={className} aria-hidden="true"
    >
      <path d="M12 17v5M9 3h6l-1 6 3.5 3.5a1 1 0 0 1-.7 1.5H7.2a1 1 0 0 1-.7-1.5L10 9 9 3Z" />
    </svg>
  );
}

/** Glifo de livro dos chips de fonte. Dimensão intrínseca em 1em: a garantia mora no
 *  elemento, não no default de classe (a cicatriz das 34 setas gigantes). */
function IconeLivro({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      strokeLinecap="round" strokeLinejoin="round"
      width="1em" height="1em" className={className} aria-hidden="true"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V4a1 1 0 0 0-1-1H6.5A2.5 2.5 0 0 0 4 5.5v14Z" />
      <path d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20v-5" />
    </svg>
  );
}
